import { ThemeEvents } from '@theme/events';

/* HBA: INTERNATIONAL COLLECTION PRICE + MOBILE SORT CONTROLLER */

const HBA_MC_DOMESTIC_COUNTRY = 'EG';
const HBA_MC_BASE_CURRENCY = 'EGP';

const HBA_MC_FX_CACHE_KEY =
  'hba-market-collection-fx-v1';

const HBA_MC_FX_CACHE_TTL =
  24 * 60 * 60 * 1000;

const HBA_MC_FX_ENDPOINTS = [
  'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/egp.min.json',
  'https://latest.currency-api.pages.dev/v1/currencies/egp.min.json',
];

let hbaMcRatesPromise = null;

const HBA_COLLECTION_MIN_PARAM =
  'hba_price_min';

const HBA_COLLECTION_MAX_PARAM =
  'hba_price_max';

const HBA_COLLECTION_PRICE_SORTS =
  new Set([
    'price-ascending',
    'price-descending',
  ]);

let hbaCollectionRenderToken = 0;
let hbaCollectionObserver = null;
let hbaCollectionUiScheduled = false;


/* -------------------------------------------------------------------------- */
/* COUNTRY / CURRENCY                                                         */
/* -------------------------------------------------------------------------- */

function hbaCollectionCountry() {
  const hook = document.querySelector(
    '.product-grid ' +
    '[data-hba-currency-estimate]' +
    '[data-hba-country]'
  );

  const fromHook = (
    hook?.dataset.hbaCountry || ''
  ).toUpperCase();

  if (fromHook) {
    return fromHook;
  }

  return '';
}


function hbaCollectionCurrency(country) {
  if (!country) {
    return '';
  }

  if (
    country ===
    HBA_MC_DOMESTIC_COUNTRY
  ) {
    return HBA_MC_BASE_CURRENCY;
  }

  /*
   * The existing HBA header code has already
   * converted EGP into SAR / USD / AED / etc.
   *
   * Read that display currency here.
   */
  const label =
    document.querySelector(
      '[data-testid="localization-currency-code"]'
    );

  const code = (
    label?.textContent || ''
  )
    .trim()
    .toUpperCase();

  return (
    code &&
    code !== HBA_MC_BASE_CURRENCY
  )
    ? code
    : '';
}


/* -------------------------------------------------------------------------- */
/* CURRENCY FORMATTING                                                        */
/* -------------------------------------------------------------------------- */

function hbaMcFormatCurrency(
  value,
  currency
) {
  try {
    return new Intl.NumberFormat(
      document.documentElement.lang || 'en',
      {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }
    ).format(value);
  } catch (_error) {
    return (
      `${Number(value).toFixed(2)} ` +
      currency
    );
  }
}


/* -------------------------------------------------------------------------- */
/* FX CACHE                                                                   */
/* -------------------------------------------------------------------------- */

function hbaMcReadCachedRates() {
  try {
    const raw =
      localStorage.getItem(
        HBA_MC_FX_CACHE_KEY
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    if (
      !parsed ||
      typeof parsed.savedAt !==
        'number' ||
      typeof parsed.rates !==
        'object'
    ) {
      return null;
    }

    if (
      Date.now() -
        parsed.savedAt >
      HBA_MC_FX_CACHE_TTL
    ) {
      return null;
    }

    return parsed.rates;
  } catch (_error) {
    return null;
  }
}


function hbaMcWriteCachedRates(
  rates
) {
  try {
    localStorage.setItem(
      HBA_MC_FX_CACHE_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        rates,
      })
    );
  } catch (_error) {
    /*
     * No problem if localStorage
     * is unavailable.
     */
  }
}


async function hbaMcFetchRates() {
  const cached =
    hbaMcReadCachedRates();

  if (cached) {
    return cached;
  }

  for (
    const endpoint of
    HBA_MC_FX_ENDPOINTS
  ) {
    try {
      const response =
        await fetch(
          endpoint,
          {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            cache: 'force-cache',
          }
        );

      if (!response.ok) {
        continue;
      }

      const payload =
        await response.json();

      const rates =
        payload?.egp;

      if (
        !rates ||
        typeof rates !==
          'object'
      ) {
        continue;
      }

      hbaMcWriteCachedRates(
        rates
      );

      return rates;
    } catch (_error) {
      /*
       * Try fallback endpoint.
       */
    }
  }

  return null;
}


function hbaMcGetRates() {
  if (!hbaMcRatesPromise) {
    hbaMcRatesPromise =
      hbaMcFetchRates();
  }

  return hbaMcRatesPromise;
}


/* -------------------------------------------------------------------------- */
/* COLLECTION CONTEXT                                                         */
/* -------------------------------------------------------------------------- */

function hbaCollectionIsInternational() {
  const country =
    hbaCollectionCountry();

  return Boolean(
    country &&
    country !==
      HBA_MC_DOMESTIC_COUNTRY
  );
}


/* -------------------------------------------------------------------------- */
/* CUSTOM PRICE FILTER URL                                                    */
/* -------------------------------------------------------------------------- */

function hbaCollectionGetUrlNumber(
  name
) {
  const raw =
    new URL(
      window.location.href
    ).searchParams.get(name);

  if (
    raw === null ||
    raw.trim() === ''
  ) {
    return null;
  }

  const value =
    Number(raw);

  return (
    Number.isFinite(value) &&
    value >= 0
  )
    ? value
    : null;
}


function hbaCollectionSetUrlNumber(
  url,
  name,
  value
) {
  if (
    value === null ||
    value === '' ||
    !Number.isFinite(
      Number(value)
    )
  ) {
    url.searchParams.delete(
      name
    );

    return;
  }

  url.searchParams.set(
    name,
    String(Number(value))
  );
}


/*
 * Generate the URL sent back to Shopify.
 *
 * HBA's custom international price filter
 * must NOT be sent to Shopify's native
 * EGP price filtering system.
 */
function hbaCollectionCleanServerUrl(
  page = 1
) {
  const url =
    new URL(
      window.location.href
    );

  url.searchParams.delete(
    HBA_COLLECTION_MIN_PARAM
  );

  url.searchParams.delete(
    HBA_COLLECTION_MAX_PARAM
  );

  url.searchParams.delete(
    'filter.v.price.gte'
  );

  url.searchParams.delete(
    'filter.v.price.lte'
  );

  url.searchParams.set(
    'page',
    String(page)
  );

  url.hash = '';

  return url;
}


/* -------------------------------------------------------------------------- */
/* PRODUCT CARD PRICE                                                         */
/* -------------------------------------------------------------------------- */

function hbaCollectionCardPriceCents(
  card
) {
  if (
    !(card instanceof Element)
  ) {
    return null;
  }

  /*
   * This comes directly from price.liquid.
   *
   * It is Shopify's contextual market/catalog
   * price BEFORE our SAR/USD/etc conversion.
   */
  const hook =
    card.querySelector(
      '[data-hba-currency-estimate]' +
      '[data-hba-amount-cents]'
    );

  if (
    !(hook instanceof HTMLElement)
  ) {
    return null;
  }

  const amount =
    Number(
      hook.dataset
        .hbaAmountCents || ''
    );

  return (
    Number.isFinite(amount) &&
    amount >= 0
  )
    ? amount
    : null;
}


function hbaCollectionProductKey(
  card,
  fallbackIndex
) {
  if (
    !(card instanceof HTMLElement)
  ) {
    return (
      `fallback-${fallbackIndex}`
    );
  }

  const productCard =
    card.querySelector(
      '[data-product-id], ' +
      'product-card'
    );

  return (
    card.dataset.productId ||
    productCard?.getAttribute(
      'data-product-id'
    ) ||
    card
      .querySelector(
        'a[href*="/products/"]'
      )
      ?.getAttribute('href') ||
    card.id ||
    `fallback-${fallbackIndex}`
  );
}


/* -------------------------------------------------------------------------- */
/* FETCH COLLECTION PAGES                                                     */
/* -------------------------------------------------------------------------- */

async function hbaCollectionFetchPage(
  page
) {
  const response =
    await fetch(
      hbaCollectionCleanServerUrl(
        page
      ).toString(),
      {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
      }
    );

  if (!response.ok) {
    throw new Error(
      'Collection page fetch ' +
      `failed: ${response.status}`
    );
  }

  const html =
    await response.text();

  return new DOMParser()
    .parseFromString(
      html,
      'text/html'
    );
}


async function hbaCollectionFetchAllCards() {
  const firstDocument =
    await hbaCollectionFetchPage(
      1
    );

  const firstGrid =
    firstDocument.querySelector(
      '.product-grid' +
      '[data-testid="product-grid"]'
    );

  if (!firstGrid) {
    return {
      cards: [],
      maxCents: 0,
    };
  }

  const lastPage =
    Math.max(
      1,
      Number(
        firstGrid.dataset
          .lastPage || '1'
      )
    );

  const documents = [
    firstDocument,
  ];

  if (lastPage > 1) {
    const extraPages =
      await Promise.all(
        Array.from(
          {
            length:
              lastPage - 1,
          },
          (_, index) =>
            hbaCollectionFetchPage(
              index + 2
            )
        )
      );

    documents.push(
      ...extraPages
    );
  }

  const cards = [];
  const seen = new Set();

  let maxCents = 0;

  documents.forEach(
    (doc) => {
      const grid =
        doc.querySelector(
          '.product-grid' +
          '[data-testid="product-grid"]'
        );

      if (!grid) {
        return;
      }

      [
        ...grid.querySelectorAll(
          ':scope > ' +
          '.product-grid__item'
        ),
      ].forEach(
        (card, index) => {
          const key =
            hbaCollectionProductKey(
              card,
              cards.length +
                index
            );

          if (
            seen.has(key)
          ) {
            return;
          }

          seen.add(key);

          const priceCents =
            hbaCollectionCardPriceCents(
              card
            );

          if (
            priceCents !==
            null
          ) {
            maxCents =
              Math.max(
                maxCents,
                priceCents
              );
          }

          cards.push(card);
        }
      );
    }
  );

  return {
    cards,
    maxCents,
  };
}


/* -------------------------------------------------------------------------- */
/* PRODUCT COUNTS                                                             */
/* -------------------------------------------------------------------------- */

function hbaCollectionUpdateCounts(
  count
) {
  document
    .querySelectorAll(
      '[data-testid="products-count"] span'
    )
    .forEach(
      (element) => {
        element.textContent =
          `${count} item` +
          `${count === 1 ? '' : 's'}`;
      }
    );

  document
    .querySelectorAll(
      '.facets__see-results'
    )
    .forEach(
      (button) => {
        button.textContent =
          `See ${count} item` +
          `${count === 1 ? '' : 's'}`;
      }
    );
}


/* -------------------------------------------------------------------------- */
/* PRESERVE HBA PRICE PARAMS WHEN OTHER FILTERS CHANGE                        */
/* -------------------------------------------------------------------------- */

function hbaCollectionSyncHiddenParams() {
  const url =
    new URL(
      window.location.href
    );

  const min =
    url.searchParams.get(
      HBA_COLLECTION_MIN_PARAM
    ) || '';

  const max =
    url.searchParams.get(
      HBA_COLLECTION_MAX_PARAM
    ) || '';

  document
    .querySelectorAll(
      'facets-form-component form'
    )
    .forEach(
      (form) => {
        [
          [
            HBA_COLLECTION_MIN_PARAM,
            min,
          ],
          [
            HBA_COLLECTION_MAX_PARAM,
            max,
          ],
        ].forEach(
          ([name, value]) => {
            let input =
              form.querySelector(
                `input[data-hba-market-price-param="${name}"]`
              );

            if (
              !(
                input instanceof
                HTMLInputElement
              )
            ) {
              input =
                document.createElement(
                  'input'
                );

              input.type =
                'hidden';

              input.name =
                name;

              input.dataset
                .hbaMarketPriceParam =
                name;

              form.append(
                input
              );
            }

            input.value =
              value;
          }
        );
      }
    );
}


/* -------------------------------------------------------------------------- */
/* PRICE FILTER SUMMARY                                                       */
/* -------------------------------------------------------------------------- */

function hbaCollectionUpdatePriceFacetSummary(
  component,
  currency
) {
  const min =
    hbaCollectionGetUrlNumber(
      HBA_COLLECTION_MIN_PARAM
    );

  const max =
    hbaCollectionGetUrlNumber(
      HBA_COLLECTION_MAX_PARAM
    );

  const status =
    component
      .closest('details')
      ?.querySelector(
        '[ref="facetStatus"]'
      );

  if (
    !(
      status instanceof
      HTMLElement
    )
  ) {
    return;
  }

  let nextText = '';

  if (
    min !== null &&
    max !== null
  ) {
    nextText =
      `${hbaMcFormatCurrency(
        min,
        currency
      )} – ` +
      `${hbaMcFormatCurrency(
        max,
        currency
      )}`;
  } else if (
    min !== null
  ) {
    nextText =
      `${hbaMcFormatCurrency(
        min,
        currency
      )}+`;
  } else if (
    max !== null
  ) {
    nextText =
      `Up to ${hbaMcFormatCurrency(
        max,
        currency
      )}`;
  }

  /*
   * IMPORTANT:
   * Do not rewrite the DOM if nothing actually changed.
   * Rewriting it would trigger our collection MutationObserver.
   */
  if (
    status.textContent.trim() !==
    nextText.trim()
  ) {
    status.textContent =
      nextText;
  }
}


/* -------------------------------------------------------------------------- */
/* CHANGE PRICE FILTER TO LOCAL CURRENCY                                      */
/* -------------------------------------------------------------------------- */

function hbaCollectionPreparePriceFacets(
  maxLocal = null
) {
  /*
   * Egypt keeps native Shopify EGP
   * filtering completely unchanged.
   */
  if (
    !hbaCollectionIsInternational()
  ) {
    return;
  }

  const country =
    hbaCollectionCountry();

  const currency =
    hbaCollectionCurrency(
      country
    );

  if (!currency) {
    return;
  }

  const minValue =
    hbaCollectionGetUrlNumber(
      HBA_COLLECTION_MIN_PARAM
    );

  const maxValue =
    hbaCollectionGetUrlNumber(
      HBA_COLLECTION_MAX_PARAM
    );

  document
    .querySelectorAll(
      'price-facet-component'
    )
    .forEach(
      (component) => {
        if (
          !(
            component instanceof
            HTMLElement
          )
        ) {
          return;
        }

        component.dataset
          .hbaMarketPriceFilter =
          'true';

        const minInput =
          component.querySelector(
            '[ref="minInput"]'
          );

        const maxInput =
          component.querySelector(
            '[ref="maxInput"]'
          );

        if (
          !(
            minInput instanceof
            HTMLInputElement
          ) ||
          !(
            maxInput instanceof
            HTMLInputElement
          )
        ) {
          return;
        }

        /*
         * Remove Shopify's native EGP
         * price parameters from these
         * international fields.
         *
         * We will filter the contextual
         * catalog prices ourselves.
         */
        [
          minInput,
          maxInput,
        ].forEach(
          (input) => {
            if (
              input.name &&
              !input.dataset
                .hbaNativeName
            ) {
              input.dataset
                .hbaNativeName =
                input.name;
            }

            input.removeAttribute(
              'name'
            );

            input.setAttribute(
              'inputmode',
              'decimal'
            );
          }
        );

        /*
        * Never reset a field while the customer
        * is actively typing inside it.
        */

        if (
        document.activeElement !==
        minInput
        ) {
        const nextMinValue =
            minValue === null
            ? ''
            : String(minValue);

        if (
            minInput.value !==
            nextMinValue
        ) {
            minInput.value =
            nextMinValue;
        }
        }

        if (
        document.activeElement !==
        maxInput
        ) {
        const nextMaxValue =
            maxValue === null
            ? ''
            : String(maxValue);

        if (
            maxInput.value !==
            nextMaxValue
        ) {
            maxInput.value =
            nextMaxValue;
        }
        }

        minInput.dataset.min =
          '0';

        if (
          maxLocal !== null
        ) {
          minInput.dataset.max =
            String(maxLocal);

          maxInput.dataset.max =
            String(maxLocal);

          maxInput.placeholder =
            String(
              Math.round(
                maxLocal * 100
              ) / 100
            );
        }

        maxInput.dataset.min =
          '0';

        /*
         * Replace EGP/LE labels with
         * SAR/USD/AED/etc.
         */
        component
            .querySelectorAll(
                '.price-facet__label'
            )
            .forEach(
                (label) => {
                /*
                * Avoid generating another
                * MutationObserver event when
                * the label is already correct.
                */
                if (
                    label.textContent.trim() !==
                    currency
                ) {
                    label.textContent =
                    currency;
                }
                }
            );

        /*
         * Highest price message.
         */
        const highest =
          component.querySelector(
            '.price-facet__highest-price'
          );

        if (
            highest instanceof
            HTMLElement
            ) {
            const nextHighestText =
                maxLocal === null
                ? `Prices shown in ${currency}`
                : (
                    `Highest price is ` +
                    hbaMcFormatCurrency(
                        maxLocal,
                        currency
                    )
                    );

            if (
                highest.textContent.trim() !==
                nextHighestText.trim()
            ) {
                highest.textContent =
                nextHighestText;
            }
            }

        hbaCollectionUpdatePriceFacetSummary(
          component,
          currency
        );

        const isActive =
          minValue !== null ||
          maxValue !== null;

        const clearButton =
          component.querySelector(
            '.facets__clear'
          );

        if (
          clearButton instanceof
          HTMLElement
        ) {
          clearButton.classList.toggle(
            'facets__clear--active',
            isActive
          );
        }

        const clearComponent =
          component.querySelector(
            'facet-clear-component'
          );

        if (
          clearComponent instanceof
          HTMLElement
        ) {
          clearComponent.setAttribute(
            'tabindex',
            isActive
              ? '0'
              : '-1'
          );
        }
      }
    );

  const url =
    new URL(
      window.location.href
    );

  const hasNativeFilters =
    [
      ...url.searchParams.keys(),
    ].some(
      (key) =>
        key.startsWith(
          'filter.'
        )
    );

  const hasMarketPriceFilter =
    minValue !== null ||
    maxValue !== null;

  document
    .querySelectorAll(
      '.facets__clear-all'
    )
    .forEach(
      (button) => {
        button.classList.toggle(
          'active',
          hasNativeFilters ||
            hasMarketPriceFilter
        );
      }
    );

  hbaCollectionSyncHiddenParams();
}


/* -------------------------------------------------------------------------- */
/* APPLY LOCAL PRICE FILTER                                                   */
/* -------------------------------------------------------------------------- */

function hbaCollectionCommitPriceFacet(
  component
) {
  if (
    !(
      component instanceof
      HTMLElement
    )
  ) {
    return;
  }

  const minInput =
    component.querySelector(
      '[ref="minInput"]'
    );

  const maxInput =
    component.querySelector(
      '[ref="maxInput"]'
    );

  if (
    !(
      minInput instanceof
      HTMLInputElement
    ) ||
    !(
      maxInput instanceof
      HTMLInputElement
    )
  ) {
    return;
  }

  let min =
    minInput.value.trim() ===
    ''
      ? null
      : Number(
          minInput.value.replace(
            ',',
            '.'
          )
        );

  let max =
    maxInput.value.trim() ===
    ''
      ? null
      : Number(
          maxInput.value.replace(
            ',',
            '.'
          )
        );

  if (
    min !== null &&
    (
      !Number.isFinite(min) ||
      min < 0
    )
  ) {
    min = null;
  }

  if (
    max !== null &&
    (
      !Number.isFinite(max) ||
      max < 0
    )
  ) {
    max = null;
  }

  if (
    min !== null &&
    max !== null &&
    min > max
  ) {
    [
      min,
      max,
    ] = [
      max,
      min,
    ];
  }

  const url =
    new URL(
      window.location.href
    );

  hbaCollectionSetUrlNumber(
    url,
    HBA_COLLECTION_MIN_PARAM,
    min
  );

  hbaCollectionSetUrlNumber(
    url,
    HBA_COLLECTION_MAX_PARAM,
    max
  );

  /*
   * Make sure Shopify's native EGP
   * price filtering is disabled.
   */
  url.searchParams.delete(
    'filter.v.price.gte'
  );

  url.searchParams.delete(
    'filter.v.price.lte'
  );

  url.searchParams.delete(
    'page'
  );

  history.pushState(
    {},
    '',
    url.toString()
  );

  hbaCollectionSyncHiddenParams();

  hbaCollectionPreparePriceFacets();

  hbaCollectionRender();
}


/* -------------------------------------------------------------------------- */
/* INTERNATIONAL FILTERING + PRICE SORTING                                    */
/* -------------------------------------------------------------------------- */

async function hbaCollectionRender() {
  if (
    !hbaCollectionIsInternational()
  ) {
    return;
  }

  const grid =
    document.querySelector(
      '.product-grid' +
      '[data-testid="product-grid"]'
    );

  if (
    !(
      grid instanceof
      HTMLElement
    )
  ) {
    return;
  }

  const token =
    ++hbaCollectionRenderToken;

  const country =
    hbaCollectionCountry();

  const currency =
    hbaCollectionCurrency(
      country
    );

  const rates =
    await hbaMcGetRates();

  if (
    !rates ||
    !currency
  ) {
    return;
  }

  const rate =
    Number(
      rates[
        currency.toLowerCase()
      ]
    );

  if (
    !Number.isFinite(rate) ||
    rate <= 0
  ) {
    return;
  }

  let payload;

  try {
    payload =
      await hbaCollectionFetchAllCards();
  } catch (error) {
    console.warn(
      'HBA collection pricing controller ' +
      'could not load all products.',
      error
    );

    return;
  }

  if (
    token !==
    hbaCollectionRenderToken
  ) {
    return;
  }

  /*
   * Highest actual contextual catalog price,
   * converted to visitor currency.
   */
  const maxLocal =
    (
      payload.maxCents /
      100
    ) * rate;

  hbaCollectionPreparePriceFacets(
    maxLocal
  );

  const minLocal =
    hbaCollectionGetUrlNumber(
      HBA_COLLECTION_MIN_PARAM
    );

  const maxLocalFilter =
    hbaCollectionGetUrlNumber(
      HBA_COLLECTION_MAX_PARAM
    );

  const sortBy =
    new URL(
      window.location.href
    ).searchParams.get(
      'sort_by'
    ) || '';

  const needsCustomRender =
    minLocal !== null ||
    maxLocalFilter !== null ||
    HBA_COLLECTION_PRICE_SORTS.has(
      sortBy
    );

  const wasCustomRendered =
    grid.dataset
      .hbaCollectionCustomRendered ===
    'true';

  if (
    !needsCustomRender &&
    !wasCustomRendered
  ) {
    return;
  }

  /*
   * Apply local-currency range.
   */
  const prepared =
    payload.cards
      .map(
        (card, index) => ({
          card,
          index,
          cents:
            hbaCollectionCardPriceCents(
              card
            ),
        })
      )
      .filter(
        (entry) => {
          if (
            entry.cents === null
          ) {
            return false;
          }

          const localPrice =
            (
              entry.cents /
              100
            ) * rate;

          if (
            minLocal !== null &&
            localPrice <
              minLocal
          ) {
            return false;
          }

          if (
            maxLocalFilter !==
              null &&
            localPrice >
              maxLocalFilter
          ) {
            return false;
          }

          return true;
        }
      );

  /*
   * Price low/high uses actual
   * international catalog prices.
   */
  if (
    HBA_COLLECTION_PRICE_SORTS.has(
      sortBy
    )
  ) {
    prepared.sort(
      (a, b) => {
        const difference =
          (a.cents ?? 0) -
          (b.cents ?? 0);

        if (
          difference === 0
        ) {
          return (
            a.index -
            b.index
          );
        }

        return (
          sortBy ===
          'price-descending'
        )
          ? -difference
          : difference;
      }
    );
  }

  /*
   * Replace currently rendered product cards.
   */
  grid
    .querySelectorAll(
      ':scope > ' +
      '.product-grid__item'
    )
    .forEach(
      (card) =>
        card.remove()
    );

  prepared.forEach(
    ({ card }, index) => {
      card.dataset.page =
        '1';

      [
        ...card.classList,
      ].forEach(
        (className) => {
          if (
            className.startsWith(
              'product-grid__item--'
            )
          ) {
            card.classList.remove(
              className
            );
          }
        }
      );

      card.classList.add(
        `product-grid__item--${index}`
      );

      grid.append(card);
    }
  );

  if (
    prepared.length === 0
  ) {
    const empty =
      document.createElement(
        'div'
      );

    empty.className =
      'hba-market-price-empty';

    empty.textContent =
      'No products match this price range.';

    empty.style.gridColumn =
      '1 / -1';

    empty.style.padding =
      '32px 0';

    empty.style.textAlign =
      'center';

    grid.append(empty);
  }

  /*
   * We've loaded the full filtered collection,
   * so disable Horizon pagination sentinels.
   */
  grid.dataset.lastPage =
    '1';

  grid.dataset
    .hbaCollectionCustomRendered =
    'true';

  const resultsList =
    grid.closest(
      'results-list'
    );

  resultsList
    ?.querySelectorAll(
      '[ref="viewMoreNext"], ' +
      '[ref="viewMorePrevious"]'
    )
    .forEach(
      (sentinel) => {
        sentinel.hidden =
          true;
      }
    );

  hbaCollectionUpdateCounts(
    prepared.length
  );
}


/* -------------------------------------------------------------------------- */
/* MOBILE SORT UI                                                             */
/* -------------------------------------------------------------------------- */

function hbaCollectionInjectStyles() {
  if (
    document.getElementById(
      'hba-collection-market-styles'
    )
  ) {
    return;
  }

  const style =
    document.createElement(
      'style'
    );

  style.id =
    'hba-collection-market-styles';

  style.textContent = `
    @media screen and (max-width: 749px) {

      /*
       * Horizon hides this toolbar when the
       * collection uses large mobile cards.
       * We need it visible for our sort control.
       */
      .collection-wrapper:has(.product-grid-mobile--large)
      .facets-mobile-wrapper.facets-controls-wrapper {
        display: flex !important;
      }

      /*
       * Compact mobile Sort control.
       */
      .hba-mobile-sort-control {
        display: flex;
        align-items: center;

        min-width: 0;
        width: auto;
        max-width: 132px;

        flex: 0 1 132px;
      }

      .hba-mobile-sort-control select {
        width: 100%;
        max-width: 132px;

        min-height: 36px;

        border:
          1px solid
          rgb(
            var(--color-foreground-rgb) /
            0.18
          );

        border-radius: 7px;

        background:
          var(--color-background);

        color:
          var(--color-foreground);

        padding:
          6px 24px
          6px 8px;

        font: inherit;

        font-size: 11px;
        line-height: 1.2;

        white-space: nowrap;

        overflow: hidden;

        text-overflow: ellipsis;
      }

      /*
       * Keep Filters + Sort + grid controls
       * inside the mobile width.
       */
      .facets-toggle {
        gap: 8px;
      }

      .facets-toggle
      .facets-mobile-wrapper {
        min-width: 0;

        gap: 6px;

        flex: 1 1 auto;
      }

      .facets-toggle__wrapper {
        flex: 0 0 auto;
      }
    }
  `;

  document.head.append(
    style
  );
}


/*
 * Add a mobile sorting select beside
 * Horizon's mobile filter controls.
 */
function hbaCollectionEnsureMobileSort() {
  const existing =
    document.querySelector(
      '.hba-mobile-sort-control'
    );

  if (
    window.innerWidth >= 750
  ) {
    existing?.remove();

    return;
  }

  const toolbar =
    document.querySelector(
      '.facets-toggle ' +
      '.facets-mobile-wrapper'
    );

  if (
    !(
      toolbar instanceof
      HTMLElement
    )
  ) {
    return;
  }

  /*
   * Horizon already renders a native
   * mobile Sort select inside the filter
   * drawer. We use its options as our
   * source of truth.
   */
  const source =
    document.querySelector(
      '#filters-drawer ' +
      'select[name="sort_by"], ' +
      'sorting-filter-component ' +
      'select[name="sort_by"]'
    );

  if (
    !(
      source instanceof
      HTMLSelectElement
    )
  ) {
    return;
  }

  let wrapper =
    existing;

  let select;

  if (
    !(
      wrapper instanceof
      HTMLElement
    )
  ) {
    wrapper =
      document.createElement(
        'div'
      );

    wrapper.className =
      'hba-mobile-sort-control';

    select =
      document.createElement(
        'select'
      );

    select.setAttribute(
      'aria-label',
      'Sort products'
    );

    wrapper.append(
      select
    );

    /*
     * Put Sort in the mobile toolbar.
     */
    toolbar.prepend(
      wrapper
    );

    select.addEventListener(
      'change',
      () => {
        const url =
          new URL(
            window.location.href
          );

        if (select.value) {
          url.searchParams.set(
            'sort_by',
            select.value
          );
        } else {
          url.searchParams.delete(
            'sort_by'
          );
        }

        url.searchParams.delete(
          'page'
        );

        /*
         * Full navigation keeps Shopify's
         * normal non-price sort behavior.
         *
         * Our controller corrects price
         * ascending/descending afterward
         * for international catalog prices.
         */
        window.location.assign(
          url.toString()
        );
      }
    );
  } else {
    select =
      wrapper.querySelector(
        'select'
      );
  }

  if (
    !(
      select instanceof
      HTMLSelectElement
    )
  ) {
    return;
  }

  const current =
    new URL(
      window.location.href
    ).searchParams.get(
      'sort_by'
    ) || '';

  select.innerHTML = '';

  /*
   * Short mobile labels to prevent
   * width/overlap problems.
   */
  const shortLabels = {
    featured:
      'Featured',

    'best-selling':
      'Best selling',

    'title-ascending':
      'A–Z',

    'title-descending':
      'Z–A',

    'price-ascending':
      'Price: Low → High',

    'price-descending':
      'Price: High → Low',

    'created-ascending':
      'Oldest',

    'created-descending':
      'Newest',

    manual:
      'Featured',

    relevance:
      'Most relevant',
  };

  [
    ...source.options,
  ].forEach(
    (option) => {
      const clone =
        option.cloneNode(
          true
        );

      if (
        clone instanceof
        HTMLOptionElement
      ) {
        clone.textContent =
          shortLabels[
            option.value
          ] ||
          option.textContent;

        clone.selected =
          option.value ===
            current ||
          (
            !current &&
            option.selected
          );
      }

      select.append(
        clone
      );
    }
  );
}


/* -------------------------------------------------------------------------- */
/* UI REFRESH                                                                 */
/* -------------------------------------------------------------------------- */

function hbaCollectionScheduleUi() {
  if (
    hbaCollectionUiScheduled
  ) {
    return;
  }

  hbaCollectionUiScheduled =
    true;

  requestAnimationFrame(
    () => {
      hbaCollectionUiScheduled =
        false;

      hbaCollectionInjectStyles();

      hbaCollectionEnsureMobileSort();

      hbaCollectionPreparePriceFacets();

      hbaCollectionSyncHiddenParams();
    }
  );
}


/* -------------------------------------------------------------------------- */
/* START CONTROLLER                                                           */
/* -------------------------------------------------------------------------- */

function hbaCollectionStartController() {
  /*
   * Only run on collection/search result
   * pages containing Horizon's product grid.
   */
  if (
    !document.querySelector(
      '.product-grid' +
      '[data-testid="product-grid"]'
    )
  ) {
    return;
  }

  /*
   * Initial UI.
   */
  hbaCollectionScheduleUi();

  if (
    hbaCollectionIsInternational()
  ) {
    hbaCollectionRender();
  }

  /*
   * Delayed passes allow the existing
   * header/localization code to finish
   * replacing EGP with SAR/USD/etc.
   */
  setTimeout(
    () => {
      hbaCollectionScheduleUi();

      if (
        hbaCollectionIsInternational()
      ) {
        hbaCollectionRender();
      }
    },
    500
  );

  setTimeout(
    () => {
      hbaCollectionScheduleUi();

      if (
        hbaCollectionIsInternational()
      ) {
        hbaCollectionRender();
      }
    },
    1500
  );


  /* ------------------------------------------------------------------------
     INTERNATIONAL PRICE FILTER INPUT
     ------------------------------------------------------------------------ */

  document.addEventListener(
    'change',
    (event) => {
      const target =
        event.target;

      if (
        !(
          target instanceof
          HTMLInputElement
        )
      ) {
        return;
      }

      const component =
        target.closest(
          'price-facet-component' +
          '[data-hba-market-price-filter="true"]'
        );

      if (
        !component ||
        !target.classList.contains(
          'price-facet__input'
        )
      ) {
        return;
      }

      /*
       * Stop Horizon from applying its
       * native EGP price filter.
       */
      event.preventDefault();

      event.stopImmediatePropagation();

      hbaCollectionCommitPriceFacet(
        component
      );
    },
    true
  );


  /*
   * Enter key in price input.
   */
  document.addEventListener(
    'keydown',
    (event) => {
      const target =
        event.target;

      if (
        !(
          target instanceof
          HTMLInputElement
        ) ||
        event.key !==
          'Enter'
      ) {
        return;
      }

      const component =
        target.closest(
          'price-facet-component' +
          '[data-hba-market-price-filter="true"]'
        );

      if (
        !component ||
        !target.classList.contains(
          'price-facet__input'
        )
      ) {
        return;
      }

      event.preventDefault();

      event.stopImmediatePropagation();

      hbaCollectionCommitPriceFacet(
        component
      );
    },
    true
  );


  /*
   * Clear international price filter.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target;

      if (
        !(
          target instanceof
          Element
        )
      ) {
        return;
      }

      const clear =
        target.closest(
          'price-facet-component' +
          '[data-hba-market-price-filter="true"] ' +
          'facet-clear-component'
        );

      if (!clear) {
        return;
      }

      event.preventDefault();

      event.stopImmediatePropagation();

      const url =
        new URL(
          window.location.href
        );

      url.searchParams.delete(
        HBA_COLLECTION_MIN_PARAM
      );

      url.searchParams.delete(
        HBA_COLLECTION_MAX_PARAM
      );

      url.searchParams.delete(
        'page'
      );

      history.pushState(
        {},
        '',
        url.toString()
      );

      hbaCollectionPreparePriceFacets();

      hbaCollectionSyncHiddenParams();

      hbaCollectionRender();
    },
    true
  );


  /* ------------------------------------------------------------------------
     HORIZON FILTER UPDATES
     ------------------------------------------------------------------------ */

  document.addEventListener(
    ThemeEvents.FilterUpdate,
    () => {
      /*
       * Horizon morphs the results asynchronously,
       * so refresh after it finishes.
       */
      setTimeout(
        hbaCollectionScheduleUi,
        80
      );

      setTimeout(
        () => {
          if (
            hbaCollectionIsInternational()
          ) {
            hbaCollectionRender();
          }
        },
        450
      );
    }
  );


  /*
   * Browser Back / Forward.
   */
  window.addEventListener(
    'popstate',
    () => {
      setTimeout(
        hbaCollectionScheduleUi,
        50
      );

      setTimeout(
        () => {
          if (
            hbaCollectionIsInternational()
          ) {
            hbaCollectionRender();
          }
        },
        150
      );
    }
  );


  /*
   * Mobile / desktop transition.
   */
  window.addEventListener(
    'resize',
    hbaCollectionScheduleUi
  );


  /*
   * Watch Horizon result morphs,
   * but only inside the collection results.
   */
  const resultsList =
    document.querySelector(
      'results-list'
    );

  if (
    resultsList &&
    !hbaCollectionObserver
  ) {
    hbaCollectionObserver =
      new MutationObserver(
        () => {
          hbaCollectionScheduleUi();
        }
      );

    hbaCollectionObserver.observe(
      resultsList,
      {
        subtree: true,
        childList: true,
      }
    );
  }
}


/* -------------------------------------------------------------------------- */
/* START                                                                      */
/* -------------------------------------------------------------------------- */

if (
  document.readyState ===
  'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    hbaCollectionStartController,
    {
      once: true,
    }
  );
} else {
  hbaCollectionStartController();
}